import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiService } from '../services/api';
import type { Sector, Service, TeamMember, Work } from '../types/api';
import { Carousel, CtaLink, Eyebrow, ImageTile, ProjectCard, Reveal, Section } from '../components/ui';
import { SectorHero, ServiceNumberCards } from '../components/sector-page';
import { TeamSpotlight } from '../components/home';
import { useNavData } from '../components/layout/useNavData';
import { capitalize, formatLocation, splitLead, summary } from '../utils/text';

const when = (w: Work) => (w.completed_date ? Date.parse(w.completed_date) : 0) || 0;

export default function SectorDetail() {
  const { id } = useParams<{ id: string }>();
  const [sector, setSector] = useState<Sector | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [works, setWorks] = useState<Work[]>([]);
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [status, setStatus] = useState<'loading' | 'ok' | 'error'>('loading');
  const { sectors: allSectors } = useNavData();

  useEffect(() => {
    if (!id) { setStatus('error'); return; }
    let alive = true;
    setStatus('loading'); setServices([]); setWorks([]);
    apiService.getSector(id)
      .then((s) => {
        if (!alive) return;
        if (!s.enable) { setStatus('error'); return; }
        setSector(s); setStatus('ok');
        // Services du secteur, puis leurs réalisations (le reste de la page n'attend pas)
        apiService.getServicesBySector(id).then(async (sv) => {
          if (!alive) return;
          setServices(sv);
          const lists = await Promise.all(sv.map((x) => apiService.getWorksByService(x.id).catch(() => [] as Work[])));
          if (!alive) return;
          const seen = new Set<string>();
          setWorks(lists.flat().filter((w) => (seen.has(w.id) ? false : (seen.add(w.id), true))).sort((a, b) => when(b) - when(a)));
        }).catch(() => {});
        apiService.getTeamMembers().then((t) => alive && setTeam(t)).catch(() => {});
      })
      .catch(() => alive && setStatus('error'));
    return () => { alive = false; };
  }, [id]);

  const { lead, rest } = useMemo(() => splitLead(sector?.description), [sector?.description]);
  const others = useMemo(() => {
    if (!sector) return [];
    const ok = allSectors.filter((s) => s.id !== sector.id && s.cover_image?.url);
    return [...ok.filter((s) => s.domain_id === sector.domain_id), ...ok.filter((s) => s.domain_id !== sector.domain_id)].slice(0, 4);
  }, [allSectors, sector]);
  const shownWorks = works.filter((w) => w.cover_image?.url);

  if (status === 'loading') {
    return <div aria-busy className="animate-pulse"><div className="h-[30rem] bg-secondary-200 md:h-[40rem]" /><div className="h-14 border-b border-line" /><div className="h-64 bg-secondary-300" /></div>;
  }
  if (status === 'error' || !sector) {
    return (
      <Section eyebrow="Secteurs" heading="Secteur introuvable" background="white">
        <p className="mb-8 max-w-xl text-lg text-ink">Ce secteur n'existe pas ou n'est plus disponible.</p>
        <CtaLink to="/services">Voir nos services</CtaLink>
      </Section>
    );
  }

  return (
    <div id="content" className="text-black">
      <SectorHero image={sector.cover_image?.url} title={sector.name} />

      {/* Accroche en grand sur fond sombre */}
      <section id="overview" className="relative overflow-hidden bg-charcoal py-16 text-white md:py-24">
        <svg aria-hidden viewBox="0 0 180 240" className="pointer-events-none absolute right-0 top-0 hidden h-56 w-40 text-brand lg:block" fill="none" stroke="currentColor" strokeWidth="3"><path d="M180 0V120L90 30L0 120" /></svg>
        <div className="site-container relative">
          <Eyebrow tone="light" className="mb-6">{sector.domain?.name ? summary(sector.domain.name, 60) : "Vue d'ensemble"}</Eyebrow>
          <Reveal>
            <p className="max-w-4xl font-sans text-[1.5rem] font-semibold leading-snug !text-white md:text-[2rem]">{lead}</p>
          </Reveal>
        </div>
      </section>

      {/* Le détail, seulement s'il y en a (certaines descriptions tiennent dans l'accroche) */}
      {rest.length > 0 && (
        <section className="bg-white py-14 md:py-20">
          <div className="site-container grid gap-10 lg:grid-cols-[1fr_1.4fr] lg:gap-20">
            <Reveal><h2 className="font-display text-[2rem] font-bold normal-case leading-[1.1] tracking-normal text-black md:text-[2.5rem]">En détail</h2></Reveal>
            <Reveal delay={0.08}><div className="space-y-5 text-[1.0625rem] leading-relaxed">{rest.map((p) => <p key={p.slice(0, 24)}>{p}</p>)}</div></Reveal>
          </div>
        </section>
      )}

      <ServiceNumberCards id="services" services={services} />

      {shownWorks.length > 0 && (
        <section id="projects" className="scroll-mt-40 bg-white py-16 md:py-24">
          <div className="site-container">
            <Carousel variant="projects" eyebrow="Projets réalisés" title="Réalisations dans ce secteur">
              {shownWorks.map((w) => (
                <ProjectCard key={w.id} to={`/works/${w.id}`} image={w.cover_image?.url} title={capitalize(w.title)} location={formatLocation(w.location)} />
              ))}
            </Carousel>
            <div className="mt-6"><CtaLink to="/portfolio">Toutes les réalisations</CtaLink></div>
          </div>
        </section>
      )}

      {team.length > 0 && <div id="team" ><TeamSpotlight members={team} /></div>}

      {others.length > 0 && (
        <div id="sectors" >
          <Section eyebrow="Explorer plus" heading="D'autres secteurs d'intervention" background="surface">
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {others.map((s, i) => <ImageTile key={s.id} to={`/secteurs/${s.id}`} image={s.cover_image?.url} title={s.name} text={summary(s.description, 90)} delay={i * 0.08} />)}
            </div>
          </Section>
        </div>
      )}
    </div>
  );
}
